import React, { useEffect, useState } from "react";
import { BookOpen, Video, Users, GraduationCap } from "lucide-react";
import { lmsGetCoursesByTeacher, lmsGetLiveClasses, lmsGetTeacherByUserId } from "../api/lmsApi";
import "../lms.css";

const getTeacherData = () => {
  try { return JSON.parse(localStorage.getItem("userData") || "{}"); }
  catch { return {}; }
};

export default function TeacherDashboard() {
  const userData    = getTeacherData();
  const adminId     = userData?.admin_id || null;
  const schoolId    = userData?.school_id || userData?.admin_id || null;
  const teacherName = userData?.admin_name || userData?.name || "Teacher";

  const [courses,     setCourses]     = useState([]);
  const [liveClasses, setLiveClasses] = useState([]);
  const [loading,     setLoading]     = useState(true);

  useEffect(() => {
    const fetchData = (tid) => {
      Promise.all([
        lmsGetCoursesByTeacher(tid).catch(() => ({ data: { data: [] } })),
        lmsGetLiveClasses({ school_id: schoolId }).catch(() => ({ data: { data: [] } })),
      ]).then(([cRes, lRes]) => {
        const myCoursesData = cRes?.data?.data || [];
        setCourses(myCoursesData);
        // Filter live classes by matching class+subject pairs from teacher's courses
        const pairs = new Set(myCoursesData.map((c) => `${c.class_id}:${c.subject_id}`));
        const all = lRes?.data?.data || [];
        setLiveClasses(pairs.size > 0 ? all.filter((lc) => pairs.has(`${lc.class_id}:${lc.subject_id}`)) : all);
      }).finally(() => setLoading(false));
    };

    const tid = userData?.teacher_id;
    if (tid) {
      fetchData(tid);
    } else if (adminId) {
      lmsGetTeacherByUserId(adminId)
        .then((res) => { if (res?.data?.id) fetchData(res.data.id); else setLoading(false); })
        .catch(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [adminId, schoolId]);

  const now = Date.now();
  const livNow  = liveClasses.filter((lc) => {
    const start = new Date(lc.scheduled_at).getTime();
    const end   = start + (lc.duration_minutes || 60) * 60000;
    return now >= start && now <= end;
  });
  const upcoming = liveClasses.filter((lc) => new Date(lc.scheduled_at).getTime() > now);

  const stats = [
    { label: "My Courses",       value: courses.length,    icon: BookOpen,     color: "#4f46e5", bg: "#eef2ff" },
    { label: "Live Now",         value: livNow.length,     icon: Video,        color: "#16a34a", bg: "#f0fdf4" },
    { label: "Upcoming Classes", value: upcoming.length,   icon: GraduationCap, color: "#f59e0b", bg: "#fffbeb" },
    { label: "Total Classes",    value: liveClasses.length, icon: Users,        color: "#0ea5e9", bg: "#f0f9ff" },
  ];

  return (
    <div style={{ padding: "0 0 32px" }}>
      {/* Welcome */}
      <div className="lms-page-header">
        <div>
          <h1 className="lms-page-title">Welcome back, {teacherName} 👋</h1>
          <p className="lms-page-subtitle">Here's an overview of your teaching activity.</p>
        </div>
      </div>

      {/* Stats */}
      {loading ? (
        <div className="crs-loading" />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 16, marginBottom: 32 }}>
          {stats.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} style={{
                background: "#fff", borderRadius: 12, padding: "20px 22px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.07)", display: "flex", alignItems: "center", gap: 16,
              }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: s.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon size={20} color={s.color} />
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: "#111827" }}>{s.value}</div>
                  <div style={{ fontSize: 13, color: "#6b7280" }}>{s.label}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Recent courses */}
      {!loading && courses.length > 0 && (
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: "#111827", marginBottom: 12 }}>My Courses</h2>
          <div className="crs-grid">
            {courses.slice(0, 6).map((c) => (
              <div key={c.id} className="crs-card" style={{ cursor: "default" }}>
                <div className="crs-card-thumb">
                  {c.thumbnail_url
                    ? <img src={c.thumbnail_url} alt={c.name} />
                    : <div className="crs-card-thumb-placeholder" style={{ display: "flex" }}><BookOpen size={28} color="#9ca3af" /></div>}
                </div>
                <div className="crs-card-body">
                  <h3 className="crs-card-title">{c.name}</h3>
                  <div className="crs-card-meta">
                    {c.class_name && <span className="crs-meta-item"><GraduationCap size={12} />{c.class_name}</span>}
                    {c.subject_name && <span className="crs-meta-item"><BookOpen size={12} />{c.subject_name}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
